import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Displays the products a visitor has recently viewed (tracked in localStorage
 * by `RecentlyViewed`). On connect it reads the stored product ids, excludes the
 * product currently being viewed, and fetches its own section via the Search
 * Section Rendering API (`?q=id:A OR id:B`) to render the product cards, then
 * reorders them by recency and reveals the section.
 */
class RecentlyViewedProducts extends HTMLElement {
  #loaded = false;

  connectedCallback() {
    // The host element starts hidden (display:none), so an IntersectionObserver
    // would never fire. Load on connect; the request is a single cheap fetch.
    this.#load();
  }

  async #load() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { sectionId, productId, url } = this.dataset;
    if (!sectionId || !url) return;

    const ids = RecentlyViewed.getProducts().filter((id) => id !== productId).slice(0, 4);
    if (ids.length === 0) return;

    const searchUrl = new URL(url, location.origin);
    searchUrl.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    searchUrl.searchParams.set('resources[type]', 'product');

    let html;
    try {
      html = await sectionRenderer.getSectionHTML(sectionId, false, searchUrl);
    } catch (e) {
      return;
    }
    if (!html) return;

    const fetched = new DOMParser().parseFromString(html, 'text/html').getElementById(`recently-viewed-grid-${sectionId}`);
    const grid = this.querySelector(`#recently-viewed-grid-${sectionId}`);
    if (!fetched || !grid) return;

    const cards = [...fetched.children].filter((node) => node instanceof HTMLElement);
    if (cards.length === 0) return;

    // The search endpoint returns results by relevance; restore recency order.
    cards.sort((a, b) => ids.indexOf(a.dataset.productId ?? '') - ids.indexOf(b.dataset.productId ?? ''));

    grid.replaceChildren(...cards);
    this.hidden = false;
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
